/**
 * Le decisioni prese in assenza di prescrizione, e la loro storia.
 *
 * **La criticita' arriva col dato.** `critical` e' un campo della risposta, non
 * un elenco di chiavi scritto qui: quali decisioni pesano sulla consegna lo
 * dice `settings_registry.CRITICAL`, e una seconda copia in questo file
 * diventerebbe la regola che sopravvive quando la prima cambia.
 *
 * **Si aggiunge una versione, non si modifica un valore.** Percio' non esiste
 * una `updateSetting`: la sola scrittura e' un `POST` sulla cronologia. Alla
 * prima consegna respinta la domanda e' quale soglia fosse in vigore quel
 * giorno, e a quella domanda risponde soltanto una cronologia.
 */
import { get, newKey, send } from '../api'
import type { MessageKey } from '../i18n/messages'
import { tx } from '../i18n/t'

export interface Setting {
  key: string
  value: unknown
  source: string
  confidence: number | null
  question_ref: string | null
  question: string | null
  rationale: string | null
  is_hypothesis: boolean
  /** Se cambiarla richiede una motivazione. Lo dichiara il servizio. */
  critical: boolean
  /** L'ultima versione registrata; zero se la storia e' vuota. */
  version: number
}

export interface SettingVersion {
  key: string
  version: number
  value: unknown
  source: string
  confidence: number | null
  question_ref: string | null
  question: string | null
  rationale: string | null
  reason: string | null
  critical: boolean
  decided_by: string | null
  actor_id: string | null
  created_at: string
}

export interface NewSettingVersion {
  value: unknown
  source: string
  confidence?: number | null
  question_ref?: string | null
  question?: string | null
  rationale?: string | null
  reason?: string | null
  decided_by?: string | null
}

/** Le origini che il servizio accetta, coi nomi con cui si leggono.
 *
 * `hypothesis` chiede un riferimento alla domanda aperta: un'ipotesi senza la
 * domanda che l'ha generata non si puo' chiudere, perche' nessuno sa piu' che
 * cosa si stava chiedendo al committente. */
export const SOURCES: { value: string }[] = [
  { value: 'customer' },
  { value: 'norm' },
  { value: 'hypothesis' },
]

const SOURCE_KEY: Record<string, MessageKey> = {
  customer: 'settings.sourceCustomer',
  norm: 'settings.sourceNorm',
  hypothesis: 'settings.sourceHypothesis',
}

export function sourceLabel(source: string): string {
  const key = SOURCE_KEY[source]
  return key !== undefined ? tx(key) : source
}

/** Il valore come si scrive in una casella e come si rilegge.
 *
 * Le decisioni non sono tutte booleane — ci sono elenchi di livelli e soglie — e
 * una casella di testo con dentro JSON e' l'unica forma che le tiene tutte senza
 * che la pagina debba conoscere il tipo di ognuna. */
export function writeValue(value: unknown): string {
  return JSON.stringify(value)
}

/** Legge il valore scritto a mano. Solleva se non e' JSON: accettare una
 *  stringa qualunque scriverebbe `"true"` dove serve `true`, e la differenza si
 *  scoprirebbe alla prima consegna convalidata col valore sbagliato. */
export function readValue(written: string): unknown {
  return JSON.parse(written) as unknown
}

export function readSettings(): Promise<Setting[]> {
  return get<Setting[]>('/settings')
}

export function readSettingHistory(key: string): Promise<SettingVersion[]> {
  return get<SettingVersion[]>(`/settings/${encodeURIComponent(key)}/versions`)
}

export function addSettingVersion(
  key: string,
  version: NewSettingVersion,
  idempotencyKey = newKey(),
): Promise<SettingVersion> {
  return send<SettingVersion>(
    `/settings/${encodeURIComponent(key)}/versions`,
    version,
    idempotencyKey,
  )
}
